// Cintas rojas arriba de todas las pantallas: lo que pide acción ya sobre los
// clientes del usuario (y las claves fiscales que ARCA rechazó). Cada una se
// puede ocultar por 24 h, 48 h, 7 días o 1 mes; vuelve antes si cambia lo que avisa.
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ActionIcon, Anchor, Group, Menu, Stack, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { IconAlertTriangle, IconChevronDown, IconChevronUp, IconX } from '@tabler/icons-react';
import type { CintaAlerta } from '@epms/shared';
import { api } from '../api.ts';

const CLAVE = 'epms:cintas-ocultas-hasta';
const HORA = 3600_000;
const PLAZOS = [
  { texto: '24 horas', ms: 24 * HORA },
  { texto: '48 horas', ms: 48 * HORA },
  { texto: '7 días', ms: 7 * 24 * HORA },
  { texto: '1 mes', ms: 30 * 24 * HORA },
];

/** id → hasta cuándo está oculta (ms) y con qué firma (si la firma cambia, vuelve). */
type Ocultas = Record<string, { hasta: number; firma: string }>;

function leerOcultas(): Ocultas {
  try {
    return JSON.parse(localStorage.getItem(CLAVE) ?? '{}') as Ocultas;
  } catch {
    return {};
  }
}

export function CintasAlerta() {
  const navegar = useNavigate();
  const [ocultas, setOcultas] = useState(leerOcultas);
  // En el celular las cintas taparían la pantalla: una barra que se despliega.
  const celular = useMediaQuery('(max-width: 48em)');
  const [desplegadas, setDesplegadas] = useState(false);
  const cintas = useQuery({ queryKey: ['cintas'], queryFn: () => api.get<CintaAlerta[]>('/api/cintas'), refetchInterval: 300_000 });
  const ahora = Date.now();
  const visibles = (cintas.data ?? []).filter((c) => {
    const o = ocultas[c.id];
    return !o || o.firma !== c.firma || o.hasta <= ahora;
  });
  if (!visibles.length) return null;

  const ocultar = (c: CintaAlerta, ms: number) => {
    // Se limpian las vencidas para que no se acumulen.
    const vigentes = Object.fromEntries(Object.entries(leerOcultas()).filter(([, o]) => o.hasta > Date.now()));
    const nuevas: Ocultas = { ...vigentes, [c.id]: { hasta: Date.now() + ms, firma: c.firma } };
    try { localStorage.setItem(CLAVE, JSON.stringify(nuevas)); } catch { /* sin almacenamiento: se oculta solo en esta pestaña */ }
    setOcultas(nuevas);
  };

  if (celular && !desplegadas) {
    return (
      <UnstyledButton onClick={() => setDesplegadas(true)} display="block" w="calc(100% + 2 * var(--mantine-spacing-md))"
        mx="calc(var(--mantine-spacing-md) * -1)" mt="calc(var(--mantine-spacing-md) * -1)" mb="md">
        <Group bg="red.7" c="white" px="md" py={3} gap={6} wrap="nowrap" role="alert">
          <IconAlertTriangle size={13} style={{ flexShrink: 0 }} />
          <Text fz={11.5} fw={600} style={{ flex: 1 }}>{visibles.length === 1 ? visibles[0]!.titulo : `${visibles.length} alertas`}</Text>
          <IconChevronDown size={14} />
        </Group>
      </UnstyledButton>
    );
  }

  return (
    <Stack gap={1} mx="calc(var(--mantine-spacing-md) * -1)" mt="calc(var(--mantine-spacing-md) * -1)" mb="md" role="alert">
      {visibles.map((c) => (
        // Una línea: título y detalle cortados con "…"; el texto completo, al pasar el mouse.
        <Group key={c.id} bg="red.7" c="white" px="md" py={2} gap={6} wrap="nowrap">
          <IconAlertTriangle size={13} style={{ flexShrink: 0 }} />
          <Tooltip label={`${c.titulo}: ${c.detalle}`} multiline maw={520} openDelay={300} withinPortal>
            <Text fz={11.5} lh={1.6} truncate="end" style={{ flex: 1, minWidth: 0 }}>
              <b>{c.titulo}</b> · {c.detalle}
            </Text>
          </Tooltip>
          {c.enlace && (
            <Anchor fz={11.5} c="white" underline="always" style={{ whiteSpace: 'nowrap', flexShrink: 0 }} onClick={() => navegar(c.enlace!.ruta)}>
              {c.enlace.texto}
            </Anchor>
          )}
          <Menu position="bottom-end" withinPortal shadow="md">
            <Menu.Target>
              <ActionIcon size="xs" variant="transparent" c="white" aria-label="Ocultar esta alerta" title="Ocultar esta alerta">
                <IconX size={13} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>Ocultar por</Menu.Label>
              {PLAZOS.map((p) => <Menu.Item key={p.texto} onClick={() => ocultar(c, p.ms)}>{p.texto}</Menu.Item>)}
            </Menu.Dropdown>
          </Menu>
        </Group>
      ))}
      {celular && (
        <UnstyledButton onClick={() => setDesplegadas(false)}>
          <Group bg="red.8" c="white" px="md" py={3} justify="center" gap={4}><Text size="xs">Plegar</Text><IconChevronUp size={12} /></Group>
        </UnstyledButton>
      )}
    </Stack>
  );
}
